/**
 * Recharts implementations of the Chart interface.
 *
 * Dispatch table: maps each chart type to its implementation. Types without
 * an implementation throw a clear "Week N" error pointing back to the plan.
 *
 * Geo (per Option B in the plan): Recharts cannot render a bubble-encoded
 * world map without re-implementing d3-geo inside Recharts. Geo type
 * delegates to the Visx implementation via a re-export from this dispatcher
 * (handled inside Chart.tsx — geo is the one type Recharts does not own).
 */

import type { FullChartProps } from '../types';
import { RechartsBar } from './Bar';
import { RechartsPie } from './Pie';
import { RechartsLine } from './Line';
import { RechartsStackedBar } from './StackedBar';
import { RechartsGroupedBar } from './GroupedBar';
import { RechartsCombo } from './Combo';

export function renderRechartsChart(props: FullChartProps): JSX.Element {
  switch (props.type) {
    case 'bar':
      return <RechartsBar {...props} />;
    case 'pie':
    case 'donut':
      return <RechartsPie {...props} />;
    case 'line':
    case 'area':
      return <RechartsLine {...props} />;
    case 'stacked-bar':
      return <RechartsStackedBar {...props} />;
    case 'grouped-bar':
      return <RechartsGroupedBar {...props} />;
    case 'combo':
      return <RechartsCombo {...props} />;
    case 'geo':
      // Option B per plan: delegate to Visx.
      throw new Error(
        `Recharts has no native geo support; the Chart wrapper should route 'geo' to Visx. ` +
          `Indicates Chart.tsx geo-fallback wiring is missing.`
      );
    default:
      throw new Error(
        `Recharts implementation for chart type '${props.type}' not yet wired. Land in Week 3.`
      );
  }
}
